import {
  CustoGeoResponse,
  MatrizEmpresaResponse,
  MatrizLigacaoResponse,
  VisaoMatrizResponse,
} from '@interfaces/corporate-registry/responses/rede-visoes.response';
import { VaultFormatCurrencyPipe } from '@shared/pipes/vault-format-currency.pipe';
import { CORES_GRAFO } from './registry-graph-geometry';

/**
 * O id do sócio carrega nome e documento, então a chave da célula precisa de um separador que não
 * aparece nos dados da Receita — daí o caractere de controle U+0001 (mesma escolha do Dossiê).
 */
export const SEPARADOR_CELULA = String.fromCharCode(1);

export function chaveCelula(socioId: string, cnpj: string): string {
  return `${socioId}${SEPARADOR_CELULA}${cnpj}`;
}

/** Índice das células marcadas, para o lookup ser O(1) no laço da grade. */
export function indexarLigacoes(ligacoes: MatrizLigacaoResponse[]): {
  marcadas: Set<string>;
  qualificacao: Map<string, string | null>;
} {
  const marcadas = new Set<string>();
  const qualificacao = new Map<string, string | null>();
  for (const l of ligacoes ?? []) {
    const chave = chaveCelula(l.socioId, l.cnpj);
    marcadas.add(chave);
    qualificacao.set(chave, l.qualificacao);
  }
  return { marcadas, qualificacao };
}

export function corDaEmpresaMatriz(e: MatrizEmpresaResponse, destacarRaiz = true): string {
  if (e.alerta) return CORES_GRAFO.alerta;
  if (e.raiz && destacarRaiz) return CORES_GRAFO.raiz;
  return CORES_GRAFO.empresa;
}

export function textoOcultosMatriz(v: VisaoMatrizResponse): string | null {
  const { empresas, socios } = v.ocultos ?? { empresas: 0, socios: 0 };
  if (empresas <= 0 && socios <= 0) return null;
  const fora = [
    socios > 0 ? `${socios} sócio(s)` : null,
    empresas > 0 ? `${empresas} empresa(s)` : null,
  ]
    .filter(Boolean)
    .join(' e ');
  return (
    `Matriz limitada a ${v.empresas.length} empresas × ${v.socios.length} sócios para continuar legível. ` +
    `${fora} fora da grade — os exibidos são os que mais conectam a rede. Use o mapa para ver tudo.`
  );
}

/** Rodapé "Custo do mapa" da visão Geo — o geocoding é pago, e a tela mostra quanto custou. */
export function resumoCustoGeo(
  custo: CustoGeoResponse,
  semCoordenada: number
): { partes: string[]; estimativa: string } {
  const partes = [
    `${custo.geocodesEndereco} geocode(s) de endereço`,
    `${custo.geocodesCidade} de cidade`,
    `${custo.cacheHits} do cache (grátis)`,
  ];
  if (semCoordenada > 0) partes.push(`${semCoordenada} sem localização`);

  const valor = new VaultFormatCurrencyPipe().transform(custo.estimadoUsd ?? 0, 4);
  const tudoCache = custo.geocodesEndereco + custo.geocodesCidade === 0;
  return { partes, estimativa: `estimativa ≈ US$ ${valor}${tudoCache ? ' (tudo do cache)' : ''}` };
}
